import type {
  CompanionV2Attachment,
  CompanionV2Message,
} from "./use-companion-v2";

export interface RawEvent {
  id: string;
  type: string;
  content: unknown | null;
  processedAt: unknown;
  companionsUsed?: readonly string[];
}

const TOOL_CALL_RE = /\[tool:([^\]]+)\]/;

// Tool calls we hide from the user — internal/noisy invocations that
// don't carry meaningful progress signal. Entries can be bare tool
// names ("read") or `name.action` keys ("memory.get") when only some
// actions of a tool are noise. BE sends `tool_action` as "" when the
// tool has no action concept (e.g. `bash`, `notes`); the falsy guard
// below treats "" as "no action" and falls through to the bare check.
const HIDDEN_TOOL_KEYS = new Set(["read", "memory.get"]);

export function shouldHideToolUse(
  toolName: string,
  toolAction?: string | null,
): boolean {
  if (HIDDEN_TOOL_KEYS.has(toolName)) return true;
  if (
    toolAction &&
    HIDDEN_TOOL_KEYS.has(`${toolName}.${toolAction}`)
  ) {
    return true;
  }
  return false;
}

const COMPANION_IDENTITIES = [
  "summi",
  "sage",
  "quill",
  "otto",
  "calculations",
] as const;
export type CompanionIdentity = (typeof COMPANION_IDENTITIES)[number];

export type ToolCallState = "running" | "done" | "error";

export interface ToolCall {
  id: string;
  tool: string;
  state: ToolCallState;
  timestamp: Date;
  companion?: CompanionIdentity;
  // Custom tools dispatch via an `action` field on tool_input (e.g.
  // `notes.create`, `patients.attach_notes`). Carried on both historical
  // events and live `companion-tool-use` WS events (BE schema:
  // `CompanionToolUseData.tool_action`). Optional because built-in
  // (Anthropic) tools have no action — those fall back to the bare label.
  action?: string;
  // Backend doesn't currently emit a per-tool detail string; left
  // optional so the UI can hide the line when absent and the field is
  // ready when the backend grows it.
  detail?: string;
}

function isCompanionIdentity(
  value: unknown,
): value is CompanionIdentity {
  return (
    typeof value === "string" &&
    (COMPANION_IDENTITIES as readonly string[]).includes(value)
  );
}

export function normalizeCompanionType(
  value: unknown,
): CompanionIdentity | undefined {
  if (typeof value !== "string") return undefined;
  const lower = value.toLowerCase();
  return isCompanionIdentity(lower) ? lower : undefined;
}

export function normalizeCompanionList(
  values: readonly unknown[],
): CompanionIdentity[] {
  const out: CompanionIdentity[] = [];
  for (const v of values) {
    const norm = normalizeCompanionType(v);
    if (norm && !out.includes(norm)) out.push(norm);
  }
  return out;
}

function parseTimestamp(value: unknown): Date {
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function extractText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!isRecord(content)) return "";

  if (typeof content.text === "string") return content.text;
  if (typeof content.content === "string") return content.content;

  if (Array.isArray(content.content)) {
    return content.content
      .map((block) =>
        isRecord(block) && typeof block.text === "string"
          ? block.text
          : "",
      )
      .filter(Boolean)
      .join("");
  }

  return "";
}

function extractToolInfo(
  content: unknown,
): { toolName: string; toolAction: string | null } | null {
  if (typeof content === "string") {
    const match = content.match(TOOL_CALL_RE);
    const name = match ? match[1] : content;
    return { toolName: name, toolAction: null };
  }
  if (isRecord(content) && typeof content.name === "string") {
    const action =
      isRecord(content.input) &&
      typeof content.input.action === "string"
        ? content.input.action
        : null;
    return { toolName: content.name, toolAction: action };
  }
  return null;
}

/**
 * Convert a page of historical `agentSessionEvents` into the message
 * shape the v2 chat UI renders.
 *
 * Anthropic emits tool_use events *before* the final agent.message of a
 * turn (agent decides to use a tool, then responds with the result), so
 * we forward-merge: buffer pending tool names and attach them to the
 * next agent.message. This matches the live stream, which accumulates
 * tool calls onto a single assistant bubble per turn.
 *
 * `session.*` events are dropped (system-only, not user-visible).
 * Trailing tool_uses (no following agent.message) surface as an
 * assistant bubble so paused-on-permission turns stay visible.
 */
export function mapEventsToMessages(
  events: readonly RawEvent[],
  attachmentsByEventId?: ReadonlyMap<string, CompanionV2Attachment[]>,
  clientResolvedToolUseIds?: ReadonlySet<string>,
  erroredToolUseIds?: ReadonlySet<string>,
): CompanionV2Message[] {
  const messages: CompanionV2Message[] = [];
  let pendingToolCalls: ToolCall[] = [];

  const resolvedToolUseIds = new Set<string>(
    clientResolvedToolUseIds ?? [],
  );
  for (const event of events) {
    if (event.type === "user.tool_confirmation") {
      const id = extractStringField(event.content, "tool_use_id");
      if (id) resolvedToolUseIds.add(id);
    } else if (event.type === "user.custom_tool_result") {
      const id = extractStringField(
        event.content,
        "custom_tool_use_id",
      );
      if (id) resolvedToolUseIds.add(id);
    }
  }

  const attachmentsFor = (
    eventId: string,
  ): CompanionV2Attachment[] | undefined => {
    const found = attachmentsByEventId?.get(eventId);
    return found && found.length ? found : undefined;
  };

  for (const event of events) {
    const timestamp = parseTimestamp(event.processedAt);

    if (event.type === "user.message") {
      const text = extractText(event.content);
      const attachments = attachmentsFor(event.id);
      // Keep the bubble if there are files even with no text (e.g.
      // attachment-only send once the composer allows it).
      if (!text && !attachments) continue;
      messages.push({
        id: event.id,
        role: "user",
        text,
        timestamp,
        attachments,
      });
      continue;
    }

    if (event.type === "agent.message") {
      const text = extractText(event.content);
      const attachments = attachmentsFor(event.id);
      if (!text && !attachments) continue;
      const companions = Array.isArray(event.companionsUsed)
        ? normalizeCompanionList(event.companionsUsed)
        : [];
      // BE persists companions_used as []on tool_use events and only
      // writes the cumulative list on agent.message. Forward-merge the
      // most recent companion onto the buffered tool calls so historical
      // tools attribute correctly instead of falling back to Otto.
      const latest = companions[companions.length - 1];
      const attributedTools = latest
        ? pendingToolCalls.map((tc) =>
            tc.companion ? tc : { ...tc, companion: latest },
          )
        : pendingToolCalls;
      messages.push({
        id: event.id,
        role: "assistant",
        text,
        timestamp,
        companions: companions.length ? companions : undefined,
        toolCalls: attributedTools.length
          ? attributedTools
          : undefined,
        attachments,
      });
      pendingToolCalls = [];
      continue;
    }

    if (event.type === "user.interrupt") {
      messages.push({
        id: event.id,
        role: "user",
        kind: "interrupt",
        text: "",
        timestamp,
      });
      pendingToolCalls = [];
      continue;
    }

    if (
      event.type === "agent.tool_use" ||
      event.type === "agent.custom_tool_use"
    ) {
      const tool = extractToolInfo(event.content);
      if (!tool) continue;
      if (shouldHideToolUse(tool.toolName, tool.toolAction)) continue;
      pendingToolCalls.push({
        id: event.id,
        tool: tool.toolName,
        action: tool.toolAction ?? undefined,
        state: "done",
        timestamp,
      });
      continue;
    }

    // session.* and any unknown types are dropped — not user-visible.
  }

  if (pendingToolCalls.length > 0) {
    const restated = pendingToolCalls.map<ToolCall>((tc) => {
      if (erroredToolUseIds?.has(tc.id))
        return { ...tc, state: "error" };
      if (resolvedToolUseIds.has(tc.id)) return tc;
      return { ...tc, state: "running" };
    });
    const last = restated[restated.length - 1];
    messages.push({
      id: `pending-tool-bubble-${last.id}`,
      role: "assistant",
      text: "",
      timestamp: last.timestamp,
      toolCalls: restated,
    });
  }

  return messages;
}

export function extractTrailingToolUses(
  events: readonly RawEvent[],
): {
  toolCalls: ToolCall[];
  personas: CompanionIdentity[];
} {
  let startIdx = events.length;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].type === "agent.message") break;
    startIdx = i;
  }
  const toolCalls: ToolCall[] = [];
  const personas: CompanionIdentity[] = [];
  const resolvedIds = new Set<string>();
  for (let i = startIdx; i < events.length; i++) {
    const event = events[i];
    if (event.type === "user.tool_confirmation") {
      const id = extractStringField(event.content, "tool_use_id");
      if (id) resolvedIds.add(id);
      continue;
    }
    if (event.type === "user.custom_tool_result") {
      const id = extractStringField(
        event.content,
        "custom_tool_use_id",
      );
      if (id) resolvedIds.add(id);
      continue;
    }
    if (
      event.type !== "agent.tool_use" &&
      event.type !== "agent.custom_tool_use"
    ) {
      continue;
    }
    const tool = extractToolInfo(event.content);
    if (!tool) continue;
    if (shouldHideToolUse(tool.toolName, tool.toolAction)) continue;
    const persona =
      isRecord(event.content) &&
      typeof event.content.companion_type === "string"
        ? normalizeCompanionType(event.content.companion_type)
        : undefined;
    if (persona && !personas.includes(persona))
      personas.push(persona);
    toolCalls.push({
      id: event.id,
      tool: tool.toolName,
      action: tool.toolAction ?? undefined,
      state: "done",
      timestamp: parseTimestamp(event.processedAt),
      companion: persona,
    });
  }
  if (toolCalls.length > 0) {
    const last = toolCalls[toolCalls.length - 1];
    if (!resolvedIds.has(last.id)) {
      toolCalls[toolCalls.length - 1] = { ...last, state: "running" };
    }
  }
  return { toolCalls, personas };
}

function extractStringField(
  content: unknown,
  key: string,
): string | null {
  if (!isRecord(content)) return null;
  const v = content[key];
  return typeof v === "string" ? v : null;
}
